import React, { useRef, useState, useEffect } from 'react';

interface CropEditorModalProps {
  /** Object URL of the image to crop */
  imageUrl: string;
  /** Called with the cropped JPEG File (white background) */
  onConfirm: (file: File) => void;
  /** Called when user cancels */
  onCancel: () => void;
  cropW?: number;
  cropH?: number;
}

const CropEditorModal: React.FC<CropEditorModalProps> = ({
  imageUrl,
  onConfirm,
  onCancel,
  cropW = 680,
  cropH = 320,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragFrom, setDragFrom] = useState({ x: 0, y: 0, ox: 0, oy: 0 });

  // Load image and init scale/offset
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      const initScale = Math.max(cropW / img.width, cropH / img.height);
      const s = Math.max(0.3, Math.min(initScale, 4));
      setScale(s);
      setOffset({ x: (cropW - img.width * s) / 2, y: (cropH - img.height * s) / 2 });
    };
    img.src = imageUrl;
  }, [imageUrl, cropW, cropH]);

  // Redraw canvas — white background always
  useEffect(() => {
    if (!imgEl || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, cropW, cropH);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cropW, cropH);
    ctx.drawImage(imgEl, offset.x, offset.y, imgEl.width * scale, imgEl.height * scale);
  }, [imgEl, scale, offset, cropW, cropH]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setDragFrom({ x: e.clientX, y: e.clientY, ox: offset.x, oy: offset.y });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    e.preventDefault();
    setOffset({ x: dragFrom.ox + (e.clientX - dragFrom.x), y: dragFrom.oy + (e.clientY - dragFrom.y) });
  };
  const handleMouseUp = (e: React.MouseEvent) => { e.preventDefault(); setDragging(false); };
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const factor = e.deltaY < 0 ? 1.08 : 0.93;
    const ns = Math.max(0.2, Math.min(5, scale * factor));
    const r = ns / scale;
    setScale(ns);
    setOffset(prev => ({
      x: cropW / 2 - (cropW / 2 - prev.x) * r,
      y: cropH / 2 - (cropH / 2 - prev.y) * r,
    }));
  };

  const handleConfirm = () => {
    if (!canvasRef.current) return;
    const srcCanvas = canvasRef.current;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = cropW;
    exportCanvas.height = cropH;
    const ctx = exportCanvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cropW, cropH);
    ctx.drawImage(srcCanvas, 0, 0);
    exportCanvas.toBlob(blob => {
      if (!blob) return;
      onConfirm(new File([blob], 'custom-image.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        background: '#111', borderRadius: 18,
        overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        display: 'flex', flexDirection: 'column',
        maxWidth: '95vw',
      }}>
        {/* Header */}
        <div style={{
          padding: '10px 18px', background: '#1e1e2e',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ color: '#a5b4fc', fontWeight: 700, fontSize: '0.9rem' }}>
            ✂️ Crop ảnh — Kéo để di chuyển, scroll để zoom
          </span>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
          >×</button>
        </div>

        {/* Canvas */}
        <canvas
          ref={canvasRef}
          width={cropW}
          height={cropH}
          style={{
            width: '100%', maxWidth: cropW, display: 'block',
            cursor: dragging ? 'grabbing' : 'grab',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />

        {/* Actions */}
        <div style={{
          padding: '10px 18px', background: '#1e1e2e',
          display: 'flex', gap: 10, justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 20px', borderRadius: 9, border: '1px solid #334155',
              background: 'transparent', color: '#94a3b8', fontWeight: 600,
              fontSize: '0.88rem', cursor: 'pointer',
            }}
          >Hủy</button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '8px 24px', borderRadius: 9, border: 'none',
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
              color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
            }}
          >✅ Xác nhận crop</button>
        </div>
      </div>
    </div>
  );
};

export default CropEditorModal;
